import { Suspense } from 'react'
import { createBrowserRouter, Navigate, useParams, useLocation } from 'react-router-dom'
import { AppLayout } from '@/shared/components/layout/AppLayout'
import { ProtectedRoute } from '@/shared/components/layout/ProtectedRoute'
import { RoleGuard } from '@/shared/components/layout/RoleGuard'
import { HomeRedirect } from '@/shared/components/layout/HomeRedirect'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { RootScrollLayout } from '@/shared/components/ScrollToTopOnNavigate'
import { ForceLightTheme } from '@/shared/components/ForceLightTheme'
import { SystemTheme } from '@/shared/components/SystemTheme'
import { lazyWithRecovery } from '@/lib/lazyWithRecovery'
import { COMPARISON_REGISTRY } from '@/features/landing/content/comparison/registry'
import { MARKET_LANDINGS } from '@/features/landing/lib/marketingPages'
// The eleven routes a member can pick as their landing page declare their guards in
// one shared table, because `HomeRedirect` has to consult the very same one. A guard
// here that disagrees with what HomeRedirect believes is not a wrong landing page —
// it is a sign-in loop that locks the member out (finding F13). See homePages.ts.
import { HOME_PAGE_ROUTES as HOME } from '@/router/homePages'
import { AUTH } from '@/shared/access/accessModel'
// Eagerly-loaded pages — small, on the critical auth/error path. Keeping
// them in the main bundle avoids a second round-trip on the very first screen
// a visitor or locked-out user sees.
import { LoginPage } from '@/features/auth/pages/LoginPage'
import { NotFoundPage } from '@/shared/components/NotFoundPage'

// ── Lazy-loaded pages ─────────────────────────────────────────────────────
// Every page below ships as its own chunk. TanStack Query + Recharts + PDF
// libs pull large transitive deps onto heavier screens (Finance, Dashboard,
// detail pages), so keeping them out of the initial bundle is a clear win.
// lazyWithRecovery wraps React.lazy with stale-chunk recovery: on a chunk
// 404 after a deploy it unregisters SWs, clears CacheStorage, and reloads
// so the next navigation fetches fresh HTML over the network (not from any
// cache layer). See lib/lazyWithRecovery.ts for the full strategy.

const RegisterPage            = lazyWithRecovery(() => import('@/features/auth/pages/RegisterPage'), 'RegisterPage')
const AcceptInvitePage        = lazyWithRecovery(() => import('@/features/auth/pages/AcceptInvitePage'), 'AcceptInvitePage')
const NoStudioPage            = lazyWithRecovery(() => import('@/features/auth/pages/NoStudioPage'), 'NoStudioPage')
const CreateStudioPage        = lazyWithRecovery(() => import('@/features/auth/pages/CreateStudioPage'), 'CreateStudioPage')
const ConfirmEmailChangePage  = lazyWithRecovery(() => import('@/features/auth/pages/ConfirmEmailChangePage'), 'ConfirmEmailChangePage')
const ConfirmLeaveStudioPage  = lazyWithRecovery(() => import('@/features/auth/pages/ConfirmLeaveStudioPage'), 'ConfirmLeaveStudioPage')
const ConfirmEmailPage        = lazyWithRecovery(() => import('@/features/auth/pages/ConfirmEmailPage'), 'ConfirmEmailPage')
const CheckoutConfirmPage     = lazyWithRecovery(() => import('@/features/auth/pages/CheckoutConfirmPage'), 'CheckoutConfirmPage')
const ConfirmRoleChangePage   = lazyWithRecovery(() => import('@/features/auth/pages/ConfirmRoleChangePage'), 'ConfirmRoleChangePage')
const DashboardPage           = lazyWithRecovery(() => import('@/features/dashboard/pages/DashboardPage'), 'DashboardPage')
const AttentionPage           = lazyWithRecovery(() => import('@/features/dashboard/pages/AttentionPage'), 'AttentionPage')
const EmailsPage              = lazyWithRecovery(() => import('@/features/inbox/pages/EmailsPage'), 'EmailsPage')
const InboxPage               = lazyWithRecovery(() => import('@/features/inbox/pages/InboxPage'), 'InboxPage')
const EmailSignaturesPage     = lazyWithRecovery(() => import('@/features/inbox/pages/EmailSignaturesPage'), 'EmailSignaturesPage')
const EmailTemplatesPage      = lazyWithRecovery(() => import('@/features/inbox/pages/EmailTemplatesPage'), 'EmailTemplatesPage')
const EmailAutoRepliesPage    = lazyWithRecovery(() => import('@/features/inbox/pages/EmailAutoRepliesPage'), 'EmailAutoRepliesPage')
const OutboxPage              = lazyWithRecovery(() => import('@/features/inbox/pages/OutboxPage'), 'OutboxPage')
const ClientsPage             = lazyWithRecovery(() => import('@/features/clients/pages/ClientsPage'), 'ClientsPage')
const ClientDetailPage        = lazyWithRecovery(() => import('@/features/clients/pages/ClientDetailPage'), 'ClientDetailPage')
// No exportName: ImportClientsWizard has only a default export. Naming an
// export the module does not have made lazyWithRecovery read the miss as a
// stale chunk — it cleared the caches, force-reloaded, and then held Suspense
// on a never-resolving promise, so /clients/import was a permanent "Loading…".
const ImportClientsWizardPage = lazyWithRecovery(() => import('@/features/clients/import/ImportClientsWizard'))
const TeamPage                = lazyWithRecovery(() => import('@/features/team/pages/TeamPage'), 'TeamPage')
const ProjectsPage            = lazyWithRecovery(() => import('@/features/projects/pages/ProjectsPage'), 'ProjectsPage')
const ProjectDetailPage       = lazyWithRecovery(() => import('@/features/projects/pages/ProjectDetailPage'), 'ProjectDetailPage')
const OffersPage              = lazyWithRecovery(() => import('@/features/offers/pages/OffersPage'), 'OffersPage')
const OfferDetailPage         = lazyWithRecovery(() => import('@/features/offers/pages/OfferDetailPage'), 'OfferDetailPage')
const ContractsPage           = lazyWithRecovery(() => import('@/features/contracts/pages/ContractsPage'), 'ContractsPage')
const ContractDetailPage      = lazyWithRecovery(() => import('@/features/contracts/pages/ContractDetailPage'), 'ContractDetailPage')
const InvoicesPage            = lazyWithRecovery(() => import('@/features/invoices/pages/InvoicesPage'), 'InvoicesPage')
const InvoiceDetailPage       = lazyWithRecovery(() => import('@/features/invoices/pages/InvoiceDetailPage'), 'InvoiceDetailPage')
const QuestionnairesPage      = lazyWithRecovery(() => import('@/features/questionnaires/pages/QuestionnairesPage'), 'QuestionnairesPage')
const QuestionnaireDetailPage = lazyWithRecovery(() => import('@/features/questionnaires/pages/QuestionnaireDetailPage'), 'QuestionnaireDetailPage')
const FinancePage             = lazyWithRecovery(() => import('@/features/finance/pages/FinancePage'), 'FinancePage')
const SettingsPage            = lazyWithRecovery(() => import('@/features/settings/pages/SettingsPage'), 'SettingsPage')
const PublicQuestionnairePage = lazyWithRecovery(() => import('@/features/publicshare/pages/PublicQuestionnairePage'), 'PublicQuestionnairePage')
const PublicContractAcceptPage = lazyWithRecovery(() => import('@/features/publicshare/pages/PublicContractAcceptPage'), 'PublicContractAcceptPage')
const PublicOfferAcceptPage   = lazyWithRecovery(() => import('@/features/publicshare/pages/PublicOfferAcceptPage'), 'PublicOfferAcceptPage')
const PublicInvoicePaymentPage = lazyWithRecovery(() => import('@/features/publicshare/pages/PublicInvoicePaymentPage'), 'PublicInvoicePaymentPage')
const OfferAdminPreviewPage   = lazyWithRecovery(() => import('@/features/publicshare/pages/OfferAdminPreviewPage'), 'OfferAdminPreviewPage')
const ContractAdminPreviewPage = lazyWithRecovery(() => import('@/features/publicshare/pages/ContractAdminPreviewPage'), 'ContractAdminPreviewPage')
const QuestionnaireAdminPreviewPage = lazyWithRecovery(() => import('@/features/publicshare/pages/QuestionnaireAdminPreviewPage'), 'QuestionnaireAdminPreviewPage')
const CalendarPage            = lazyWithRecovery(() => import('@/features/calendar/pages/CalendarPage'), 'CalendarPage')
const ShootsPage              = lazyWithRecovery(() => import('@/features/shoots/pages/ShootsPage'), 'ShootsPage')
const TasksPage               = lazyWithRecovery(() => import('@/features/tasks/pages/TasksPage'), 'TasksPage')
const ChatPage                = lazyWithRecovery(() => import('@/features/chat/pages/ChatPage'), 'ChatPage')
const ForgotPasswordPage      = lazyWithRecovery(() => import('@/features/auth/pages/ForgotPasswordPage'), 'ForgotPasswordPage')
const GuidePage               = lazyWithRecovery(() => import('@/features/landing/pages/GuidePage'), 'GuidePage')
const ResetPasswordPage       = lazyWithRecovery(() => import('@/features/auth/pages/ResetPasswordPage'), 'ResetPasswordPage')
const MyProjectsPage          = lazyWithRecovery(() => import('@/features/projects/pages/MyProjectsPage'), 'MyProjectsPage')
const MyGalleriesPage         = lazyWithRecovery(() => import('@/features/gallery/pages/MyGalleriesPage'), 'MyGalleriesPage')
const MyTasksPage             = lazyWithRecovery(() => import('@/features/tasks/pages/MyTasksPage'), 'MyTasksPage')
const PublicArchiveExportPage = lazyWithRecovery(() => import('@/features/publicshare/pages/PublicArchiveExportPage'), 'PublicArchiveExportPage')
const ClientGalleryPage       = lazyWithRecovery(() => import('@/features/publicshare/pages/ClientGalleryPage'), 'ClientGalleryPage')
const PublicIncompleteLinkPage = lazyWithRecovery(() => import('@/features/publicshare/components/PublicIncompleteLinkPage'), 'PublicIncompleteLinkPage')
const GalleryManagePage       = lazyWithRecovery(() => import('@/features/gallery/pages/GalleryManagePage'), 'GalleryManagePage')
const GalleriesPage           = lazyWithRecovery(() => import('@/features/gallery/pages/GalleriesPage'), 'GalleriesPage')
const GalleryThemePreviewPage = lazyWithRecovery(() => import('@/features/gallery/pages/GalleryThemePreviewPage'), 'GalleryThemePreviewPage')
const GalleryDesignerPage     = lazyWithRecovery(() => import('@/features/gallery/pages/GalleryDesignerPage'), 'GalleryDesignerPage')
const GalleryTemplatesListPage = lazyWithRecovery(() => import('@/features/gallery/pages/GalleryTemplatesListPage'), 'GalleryTemplatesListPage')
const GalleryTemplateEditorPage = lazyWithRecovery(() => import('@/features/gallery/pages/GalleryTemplateEditorPage'), 'GalleryTemplateEditorPage')
const OAuthCallbackPage       = lazyWithRecovery(() => import('@/features/auth/pages/OAuthCallbackPage'), 'OAuthCallbackPage')
const ConfirmDeletionPage     = lazyWithRecovery(() => import('@/features/auth/pages/ConfirmDeletionPage'), 'ConfirmDeletionPage')
const TermsPage               = lazyWithRecovery(() => import('@/features/landing/pages/TermsPage'), 'TermsPage')
const AcceptTermsPage         = lazyWithRecovery(() => import('@/features/auth/pages/AcceptTermsPage'), 'AcceptTermsPage')
const PrivacyPolicyPage       = lazyWithRecovery(() => import('@/features/landing/pages/PrivacyPolicyPage'), 'PrivacyPolicyPage')
const HelpPage                = lazyWithRecovery(() => import('@/features/landing/pages/HelpPage'), 'HelpPage')
const LandingPageV5           = lazyWithRecovery(() => import('@/features/landing/pages/LandingPageV5'), 'LandingPageV5')
// ── Public marketing surfaces (playbook WS1/WS3/WS4) ─────────────────────────
const PressPage               = lazyWithRecovery(() => import('@/features/landing/pages/PressPage'), 'PressPage')
const SubprocessorsPage       = lazyWithRecovery(() => import('@/features/landing/pages/SubprocessorsPage'), 'SubprocessorsPage')
const FoundingPage            = lazyWithRecovery(() => import('@/features/landing/pages/FoundingPage'), 'FoundingPage')
const ComparisonRoute         = lazyWithRecovery(() => import('@/features/landing/pages/ComparisonPage').then(m => ({ default: m.ComparisonRoute })))
const ShortLinkRedirect       = lazyWithRecovery(() => import('@/features/landing/pages/ShortLinkRedirect'), 'ShortLinkRedirect')
const WelcomePage1            = lazyWithRecovery(() => import('@/features/landing/pages/WelcomePage1'), 'WelcomePage1')
const AdminLoginPage          = lazyWithRecovery(() => import('@/features/admin/pages/AdminLoginPage'), 'AdminLoginPage')
const AdminDashboardPage      = lazyWithRecovery(() => import('@/features/admin/pages/AdminDashboardPage'), 'AdminDashboardPage')
const AdminCouponsPage        = lazyWithRecovery(() => import('@/features/admin/pages/AdminCouponsPage'), 'AdminCouponsPage')
const AdminSecurityPage       = lazyWithRecovery(() => import('@/features/admin/pages/AdminSecurityPage'), 'AdminSecurityPage')
const AdminLayout             = lazyWithRecovery(() => import('@/features/admin/pages/AdminLayout'), 'AdminLayout')
const AdminConfirmDeletionPage = lazyWithRecovery(() => import('@/features/admin/pages/AdminConfirmDeletionPage'), 'AdminConfirmDeletionPage')
const BookFormPage            = lazyWithRecovery(() => import('@/features/booking/pages/BookFormPage'))
const InquiriesPage           = lazyWithRecovery(() => import('@/features/booking/pages/InquiriesPage'))
const OnboardingStarterPackStep = lazyWithRecovery(() => import('@/features/auth/pages/OnboardingStarterPackStep'), 'OnboardingStarterPackStep')
const AutomationsPage          = lazyWithRecovery(() => import('@/features/automations/pages/AutomationsPage'), 'AutomationsPage')

/**
 * ADR-036 authorities used in route guards. Four levels (exactly one per membership)
 * and two switches, which carry no `ROLE_` prefix so no guard list can absorb one by
 * accident. Craft tags never appear here — they grant nothing.
 *
 * The single spelling lives in `shared/access/accessModel`; this alias only keeps the
 * route table readable.
 */
const R = AUTH

/** Redirect /tasks/:id → /tasks?highlight=:id so the detail sheet opens. */
function TaskRedirect() {
  const { id } = useParams()
  return <Navigate to={`/tasks?highlight=${id}`} replace />
}

/**
 * Redirect /inbox → /emails, carrying the query string.
 *
 * `<Navigate to="/emails" replace />` resolves a bare path, so it dropped the
 * search entirely — which meant /inbox?open=<id> arrived at the inbox with no
 * param and the thread panel never opened. Nothing noticed, because the only
 * producer of that URL is a link on a project's Emails tab.
 */
function InboxRedirect() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '/emails', search }} replace />
}

/** Minimal page-loading indicator. Kept deliberately lightweight so it can't
 *  itself contribute to layout jump while the real page's chunk downloads.
 *  Most chunks load in <200ms over a decent connection, so this is really
 *  only visible on first-paint of a new route on a cold cache / slow network. */
function PageFallback() {
  return (
    <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
      Loading…
    </div>
  )
}

/** Wrap a lazy element in Suspense with the shared fallback. Used for
 *  top-level (non-AppLayout) lazy routes that don't inherit a Suspense
 *  boundary from a parent layout. */
function suspend(element: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>
}

export const router = createBrowserRouter([
  {
    // Pathless layout route wrapping the whole tree. It resets the document
    // scroll offset on a pushed navigation (see ScrollToTopOnNavigate for why the
    // marketing pages opened at the bottom), and it is the ONE place every public
    // route passes through — so the client-facing error boundary goes here.
    //
    // Until this wrap existed the only two boundaries were inside the
    // authenticated tree, which meant a render throw on invoice-pay,
    // offer-accept, contract-accept, a gallery or the sign-in page was a blank
    // white page for somebody who cannot debug it and cannot sign in to escape it
    // (backlog R8). React picks the NEAREST boundary, so the inner ones below
    // still serve signed-in members their own diagnostics fallback; this one only
    // ever catches what they do not.
    element: (
      <ErrorBoundary audience="client">
        <RootScrollLayout />
      </ErrorBoundary>
    ),
    children: [
  {
    path: '/welcome',
    element: suspend(<LandingPageV5 />),
  },
  // Market-targeted landing URLs (RB-12 D26) — the same page with the language
  // fixed by the route: /hu is Hungarian for every browser, prerendered and
  // indexable, with CHF prices like every language. Read from the registry so the router,
  // the prerender catalogue and the sitemap test cannot disagree on the list.
  ...MARKET_LANDINGS.map((market) => ({
    path: market.path,
    element: suspend(<LandingPageV5 market={market} />),
  })),
  {
    path: '/welcome-1',
    element: suspend(<WelcomePage1 />),
  },
  {
    path: '/welcome-5',
    element: suspend(<LandingPageV5 />),
  },
  {
    path: '/login',
    element: <SystemTheme><LoginPage /></SystemTheme>,
  },
  // Public user guide — no auth required so testers and prospective users can
  // read it before logging in. Wrapped in SystemTheme (RB-12 B2): this route
  // had no theme wrapper at all, so it always rendered light regardless of
  // the visitor's OS preference or an explicit choice made elsewhere in the
  // app — the same gap SystemTheme already closes for login/register/onboarding.
  {
    path: '/guide',
    element: suspend(<SystemTheme><GuidePage /></SystemTheme>),
  },
  {
    path: '/guide/:slug',
    element: suspend(<SystemTheme><GuidePage /></SystemTheme>),
  },
  {
    path: '/register',
    element: suspend(<SystemTheme><RegisterPage /></SystemTheme>),
  },
  // Where an authenticated identity with zero studio memberships lands. NOT
  // under ProtectedRoute: that route resolves an ORG session (and would try to
  // refresh one), which is exactly what this identity does not have.
  {
    path: '/no-studio',
    element: suspend(<SystemTheme><NoStudioPage /></SystemTheme>),
  },
  // Also outside ProtectedRoute: an identity with no studio at all must be able
  // to open its first one, and create-studio is on the identity-only surface.
  {
    path: '/create-studio',
    element: suspend(<SystemTheme><CreateStudioPage /></SystemTheme>),
  },
  {
    path: '/confirm-email',
    element: suspend(<ConfirmEmailPage />),
  },
  {
    path: '/onboarding',
    element: suspend(<SystemTheme><OnboardingStarterPackStep /></SystemTheme>),
  },
  {
    path: '/invite/:token',
    element: suspend(<AcceptInvitePage />),
  },
  {
    path: '/confirm-email-change/:token',
    element: suspend(<ConfirmEmailChangePage />),
  },
  // Opened from the "confirm you want to leave" email, so it must work with no
  // session at all — the token in the path is the only credential.
  {
    path: '/confirm-leave-studio/:token',
    element: suspend(<ConfirmLeaveStudioPage />),
  },
  {
    path: '/confirm-role/:token',
    element: suspend(<ConfirmRoleChangePage />),
  },
  {
    path: '/forgot-password',
    element: suspend(<ForgotPasswordPage />),
  },
  {
    path: '/reset-password/:token',
    element: suspend(<ResetPasswordPage />),
  },
  // A client link opened WITHOUT its slug or token (cut off by a mail client, typed by hand)
  // is not the studio app's 404: that page's only exit is a "Dashboard" button that leads to
  // the studio's sign-in (PUB-22). These render a client-facing "this link isn't complete".
  ...['/q', '/contracts/accept', '/offers/accept', '/invoices/pay', '/g', '/book', '/download'].map((path) => ({
    path,
    element: suspend(<ForceLightTheme><PublicIncompleteLinkPage /></ForceLightTheme>),
  })),
  {
    path: '/q/:token',
    element: suspend(<ForceLightTheme><PublicQuestionnairePage /></ForceLightTheme>),
  },
  {
    path: '/contracts/accept/:token',
    element: suspend(<ForceLightTheme><PublicContractAcceptPage /></ForceLightTheme>),
  },
  {
    path: '/offers/accept/:token',
    element: suspend(<ForceLightTheme><PublicOfferAcceptPage /></ForceLightTheme>),
  },
  {
    path: '/invoices/pay/:token',
    element: suspend(<ForceLightTheme><PublicInvoicePaymentPage /></ForceLightTheme>),
  },
  {
    path: '/g/:slug',
    element: suspend(<ForceLightTheme><ClientGalleryPage /></ForceLightTheme>),
  },
  // The one page the "your download is ready" email and the studio bell open (IN-215 D35). A
  // sibling of AppLayout on purpose: a signed-in studio member following the bell lands here
  // like any holder of the link — no auth redirect. The key is the capability (see redactUrl).
  {
    path: '/download/:exportKey',
    element: suspend(<ForceLightTheme><PublicArchiveExportPage /></ForceLightTheme>),
  },
  // Public embeddable booking page — no auth, designed to run inside an <iframe>.
  // The only public intake surface: /inquire/:slug was deleted when intake was
  // consolidated here, and pre-go-live it gets no redirect — the route simply 404s.
  {
    path: '/book/:slug',
    element: suspend(<ForceLightTheme><BookFormPage /></ForceLightTheme>),
  },
  {
    path: '/oauth/callback',
    element: suspend(<OAuthCallbackPage />),
  },
  {
    path: '/confirm-deletion',
    element: suspend(<ConfirmDeletionPage />),
  },
  // The legal documents are language PAIRS like every other marketing document
  // (2026-09-09). The English paths are unchanged — they are in signup flows,
  // pitch emails and a DPA — and the English version is the binding one; the
  // /de halves are a convenience translation. See landing/content/legalCopy.ts.
  {
    path: '/terms',
    element: suspend(<TermsPage locale="en" />),
  },
  {
    path: '/de/terms',
    element: suspend(<TermsPage locale="de" />),
  },
  {
    path: '/accept-terms',
    element: suspend(<AcceptTermsPage />),
  },
  {
    path: '/privacy',
    element: suspend(<PrivacyPolicyPage locale="en" />),
  },
  {
    path: '/de/privacy',
    element: suspend(<PrivacyPolicyPage locale="de" />),
  },
  // ── Public marketing surfaces ────────────────────────────────────────────
  // No auth, indexable, listed in public/sitemap.xml. The comparison routes are
  // generated from the WS4 content registry so a new page is a content module
  // plus one array entry — see features/landing/content/comparison/index.ts.
  // Each document is a PAIR of URLs — English on its canonical path, German
  // under /de — and the marketing language is this prop, never the visitor's UI
  // locale. See features/landing/lib/marketingPages.ts for why, and note that
  // no marketing page may call i18n.changeLanguage(): a logged-in member's
  // uiLocale is not a public page's to rewrite.
  {
    path: '/press',
    element: suspend(<PressPage locale="en" />),
  },
  {
    path: '/de/press',
    element: suspend(<PressPage locale="de" />),
  },
  {
    path: '/legal/subprocessors',
    element: suspend(<SubprocessorsPage locale="en" />),
  },
  {
    path: '/de/legal/subprocessors',
    element: suspend(<SubprocessorsPage locale="de" />),
  },
  {
    path: '/founding',
    element: suspend(<FoundingPage locale="en" />),
  },
  {
    path: '/de/founding',
    element: suspend(<FoundingPage locale="de" />),
  },
  // Registry, not the eager content index: the articles load as their own
  // chunks inside the lazy ComparisonRoute instead of riding the entry bundle.
  ...COMPARISON_REGISTRY.map((entry) => ({
    path: entry.path,
    element: suspend(<ComparisonRoute entry={entry} />),
  })),
  // Printable short links. nginx serves a real 302 for these in production;
  // this route is the local-dev path and the safety net for an unmirrored code.
  {
    path: '/r/:code',
    element: suspend(<ShortLinkRedirect />),
  },
  {
    path: '/',
    element: <ErrorBoundary><ProtectedRoute /></ErrorBoundary>,
    children: [
      // Admin preview routes: authenticated (ProtectedRoute) but fullscreen —
      // no sidebar/header chrome so the document renders exactly like the
      // client's public view. Must be siblings of AppLayout, not children.
      // Stripe's return URL for the paid-signup flow. Authenticated (the poll is
      // a billing read) but fullscreen — the studio hasn't reached the app shell
      // yet, so no sidebar/header chrome. Sibling of AppLayout, like the previews.
      { path: 'checkout/confirm',      element: suspend(<SystemTheme><CheckoutConfirmPage /></SystemTheme>) },
      { path: 'offers/:id/preview',    element: suspend(<ForceLightTheme><OfferAdminPreviewPage /></ForceLightTheme>) },
      { path: 'contracts/:id/preview', element: suspend(<ForceLightTheme><ContractAdminPreviewPage /></ForceLightTheme>) },
      { path: 'questionnaires/:id/preview', element: suspend(<ForceLightTheme><QuestionnaireAdminPreviewPage /></ForceLightTheme>) },
      {
        // AppLayout's <Outlet /> is wrapped in its own Suspense boundary so the
        // sidebar + header stay painted while the active page chunk loads.
        element: <ErrorBoundary><AppLayout /></ErrorBoundary>,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: 'dashboard',       element: <RoleGuard allowedRoles={HOME.DASHBOARD.allowedRoles}><DashboardPage /></RoleGuard> },
          // The Needs-Attention half of the Dashboard, standalone (C5-1). Deliberately
          // NO RoleGuard: `/api/v1/attention` is isAuthenticated() and the service scopes
          // the response to the caller (MEMBER items by memberId, ORG items by the role
          // map), so every member gets their own slice and nobody else's. See AttentionPage.
          { path: 'attention',       element: <AttentionPage /> },
          { path: 'inbox',           element: <InboxRedirect /> },
          {
            path: 'emails',
            element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><EmailsPage /></RoleGuard>,
            children: [
              { index: true,            element: <InboxPage /> },
              { path: 'outbox',         element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><OutboxPage /></RoleGuard> },
              /*
               * IN-14: NO RoleGuard, deliberately. `PUT /team-members/me/signature` is
               * `isAuthenticated()`, so a member editing their OWN signature is exactly
               * who this page is for — and the Owner/Admin guard that used to sit here
               * disagreed with `EmailsPage`'s tab list (`authorities: null`), so Crew
               * were offered the tab and bounced to /my-projects on the click.
               *
               * The guard WIDENED rather than the tab narrowing, on purpose. Hiding the
               * tab would make the UI stricter than the server — the same defect class
               * `cb67a456` and `4845a4b5` spent a run removing — and Andras's call was to
               * let them in: "only show them their own private signature there, so I
               * guess split it". The split is inside the page: the studio-default
               * template and the org email-header colour render for Owner/Admin only.
               *
               * Still gated, twice over: `navGroups.ts` limits /emails to OWNER, ADMIN
               * and CREW, and EmailsPage bounces a SCOPE_ASSIGNED_ONLY member off every
               * tab but Inbox before this element mounts.
               */
              { path: 'signatures',     element: <EmailSignaturesPage /> },
              { path: 'templates',      element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><EmailTemplatesPage /></RoleGuard> },
              { path: 'auto-replies',   element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><EmailAutoRepliesPage /></RoleGuard> },
            ],
          },
          // Unscoped Crew reach the client list — `GET /clients` is
          // `isAuthenticated() and !hasAuthority('SCOPE_ASSIGNED_ONLY')`, so the server
          // has served it to them all along and only the SPA said otherwise
          // (DESIGN-what-is-crew-for.md, Q2 finding 2). `deniedRoles` mirrors the
          // annotation's negative half rather than approximating it; see
          // `navGroups.ts`'s matching row, which is the sidebar side of the same fact.
          { path: 'clients',         element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]} deniedRoles={[R.SCOPE_ASSIGNED_ONLY]}><ClientsPage /></RoleGuard> },
          // The import upserts by email: a client-existence oracle and a write past the scope for a
          // scoped member, so `ClientImportController` carries the same negative as `GET /clients`.
          { path: 'clients/import',  element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]} deniedRoles={[R.SCOPE_ASSIGNED_ONLY]}><ImportClientsWizardPage /></RoleGuard> },
          { path: 'clients/:id',     element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><ClientDetailPage /></RoleGuard> },
          { path: 'team',            element: <TeamPage /> },
          { path: 'projects',        element: <RoleGuard allowedRoles={HOME.PROJECTS.allowedRoles}><ProjectsPage /></RoleGuard> },
          { path: 'projects/:id',    element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW, R.BOOKKEEPER]}><ProjectDetailPage /></RoleGuard> },
          { path: 'galleries',       element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><GalleriesPage /></RoleGuard> },
          { path: 'calendar',        element: <RoleGuard allowedRoles={HOME.CALENDAR.allowedRoles}><CalendarPage /></RoleGuard> },
          { path: 'shoots',          element: <RoleGuard allowedRoles={HOME.SHOOTS.allowedRoles}><ShootsPage /></RoleGuard> },
          { path: 'tasks',           element: <RoleGuard allowedRoles={HOME.TASKS.allowedRoles}><TasksPage /></RoleGuard> },
          { path: 'tasks/:id',       element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW, R.BOOKKEEPER]}><TaskRedirect /></RoleGuard> },
          { path: 'chat',            element: <ChatPage /> },
          { path: 'chat/:channelId', element: <ChatPage /> },
          { path: 'offers',          element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><OffersPage /></RoleGuard> },
          { path: 'offers/:id',      element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><OfferDetailPage /></RoleGuard> },
          { path: 'contracts',       element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><ContractsPage /></RoleGuard> },
          { path: 'contracts/:id',   element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><ContractDetailPage /></RoleGuard> },
          { path: 'invoices',        element: <RoleGuard allowedRoles={HOME.INVOICES.allowedRoles}><InvoicesPage /></RoleGuard> },
          { path: 'invoices/:id',    element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.BOOKKEEPER, R.CREW]}><InvoiceDetailPage /></RoleGuard> },
          { path: 'questionnaires',      element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><QuestionnairesPage /></RoleGuard> },
          { path: 'questionnaires/:id',  element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><QuestionnaireDetailPage /></RoleGuard> },
          { path: 'finance',         element: <RoleGuard allowedRoles={HOME.FINANCE.allowedRoles}><FinancePage /></RoleGuard> },
          { path: 'settings',        element: <SettingsPage /> },
          { path: 'automations',     element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><AutomationsPage /></RoleGuard> },
          { path: 'inquiries',       element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><InquiriesPage /></RoleGuard> },
          // Legacy path — PWA shortcuts, old links, tests. Redirect to the unified tab.
          { path: 'booking-requests', element: <Navigate to="/inquiries" replace /> },
          { path: 'my-projects',     element: <RoleGuard allowedRoles={HOME.MY_PROJECTS.allowedRoles}><MyProjectsPage /></RoleGuard> },
          { path: 'my-galleries',    element: <RoleGuard allowedRoles={HOME.MY_GALLERIES.allowedRoles}><MyGalleriesPage /></RoleGuard> },
          { path: 'my-tasks',        element: <RoleGuard allowedRoles={HOME.MY_TASKS.allowedRoles}><MyTasksPage /></RoleGuard> },
          { path: 'galleries/themes', element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><GalleryThemePreviewPage /></RoleGuard> },
          { path: 'galleries/templates', element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><GalleryTemplatesListPage /></RoleGuard> },
          { path: 'galleries/templates/new', element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><GalleryTemplateEditorPage /></RoleGuard> },
          { path: 'galleries/templates/:templateId', element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN]}><GalleryTemplateEditorPage /></RoleGuard> },
          { path: 'galleries/:id/manage', element: <GalleryManagePage /> },
          { path: 'galleries/:id/design', element: <RoleGuard allowedRoles={[R.OWNER, R.ADMIN, R.CREW]}><GalleryDesignerPage /></RoleGuard> },
          { path: 'help', element: <HelpPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  {
    path: '/admin/login',
    element: suspend(<AdminLoginPage />),
  },
  {
    path: '/admin/confirm-deletion/:token',
    element: suspend(<AdminConfirmDeletionPage />),
  },
  {
    path: '/admin',
    // Its own team-audience boundary: the platform-admin realm is staff-only, so
    // the diagnostics fallback is the right one here. Without it the client
    // apology above would swallow admin errors and hide the copy-report block.
    element: <ErrorBoundary>{suspend(<AdminLayout />)}</ErrorBoundary>,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: 'coupons', element: <AdminCouponsPage /> },
      { path: 'security', element: <AdminSecurityPage /> },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
    ],
  },
])
